/**
 * UAS geographic zones (ŘLP) on the mission map — pure helpers shared by UasZonesLayer and
 * UasZonesControl: theme order and colours, the persisted theme toggles, the query of
 * GET /api/uas-zones built from the map view, and popup texts.
 *
 * API contract (GET /api/uas-zones/layers and GET /api/uas-zones):
 *   layers response: { date, data_version, layers: [{ code, name_cs, name_en, theme, min_zoom,
 *     open_categories, is_height_grid, version: { effective_from, release, imported_at, zone_count } | null }] }
 *   features response: GeoJSON FeatureCollection + { date, hidden_layers: [code] }; feature.properties:
 *     { layer, ident, vertical_limit, lower_reference, lower_m, upper_reference, upper_m, effective_from }
 *   400 with detail.error === "uas_zones_too_many" when the view holds more than the server's cap.
 */

/** Display order of the themes in the control (wire values of the layer catalogue's `theme`). */
export const UAS_ZONE_THEMES = ['aviation', 'defence', 'nature', 'infrastructure', 'population'];

/** Dense or noisy themes (rail and road buffers, power lines, HOP) start switched off. */
export const DEFAULT_ENABLED_THEMES = ['aviation', 'defence', 'nature'];

export const UAS_ZONE_THEME_COLORS = {
  aviation: { color: '#c2410c', fillColor: '#f97316' },
  defence: { color: '#991b1b', fillColor: '#dc2626' },
  nature: { color: '#15803d', fillColor: '#22c55e' },
  infrastructure: { color: '#6d28d9', fillColor: '#8b5cf6' },
  population: { color: '#a16207', fillColor: '#eab308' },
};

export const UAS_ZONES_TOO_MANY = 'uas_zones_too_many';

const STORAGE_KEY = 'fleetora_uas_zone_themes';

/** Share of the view added on each side of the requested bbox, so small pans need no new request. */
const VIEW_PADDING = 0.2;

/**
 * Colours of the height-grid cells by maximum permitted height, highest first: a value takes the first
 * step at or below it (120 m = no restriction, 0 m = no flight). Green at 120 m, green to yellow 90–110 m,
 * yellow 70–80 m, orange 40–60 m, orange to red at 30 m and below. The fill grows as the height drops,
 * only gently below 70 m (0.35 → 0.45), so the oranges and reds do not cover the map.
 */
export const GRID_HEIGHT_STEPS = [
  { minM: 120, color: '#4ade80', fillOpacity: 0.1 },
  { minM: 110, color: '#6cc644', fillOpacity: 0.2 },
  { minM: 100, color: '#9acd32', fillOpacity: 0.25 },
  { minM: 90, color: '#c6cf1f', fillOpacity: 0.3 },
  { minM: 80, color: '#facc15', fillOpacity: 0.3 },
  { minM: 70, color: '#eab308', fillOpacity: 0.35 },
  { minM: 60, color: '#fb923c', fillOpacity: 0.35 },
  { minM: 50, color: '#f97316', fillOpacity: 0.35 },
  { minM: 40, color: '#ea580c', fillOpacity: 0.37 },
  { minM: 30, color: '#fe511e', fillOpacity: 0.39 },
  { minM: 20, color: '#f93b24', fillOpacity: 0.41 },
  { minM: 10, color: '#f02e25', fillOpacity: 0.43 },
  { minM: 0, color: '#e62626', fillOpacity: 0.45 },
];

/** Grid cell whose height could not be read from the data. */
export const GRID_UNKNOWN_HEIGHT = { minM: null, color: '#6b7280', fillOpacity: 0.1 };

/** Smallest font size of the number in a grid cell; a cell too small for it gets no number. */
export const GRID_LABEL_MIN_FONT_PX = 9;
/** The cell numbers start one zoom after the grids appear (12), in one size at every zoom. */
export const GRID_LABEL_FROM_ZOOM = 13;
export const GRID_LABEL_FONT_PX = 14;

export const loadEnabledThemes = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY));
    if (Array.isArray(stored)) return UAS_ZONE_THEMES.filter((theme) => stored.includes(theme));
  } catch (e) {
    // Unreadable or blocked storage: fall back to the defaults.
  }
  return [...DEFAULT_ENABLED_THEMES];
};

export const saveEnabledThemes = (themes) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(themes));
  } catch (e) {
    // Blocked storage only loses the remembered choice.
  }
};

export const toggleTheme = (themes, theme) => (themes.includes(theme)
  ? themes.filter((t) => t !== theme)
  : UAS_ZONE_THEMES.filter((t) => t === theme || themes.includes(t)));

/** "YYYY-MM-DD" of a mission date (form value or ISO timestamp); undefined = the server's today. */
export const toDateParam = (value) => {
  if (typeof value !== 'string') return undefined;
  const match = /^(\d{4}-\d{2}-\d{2})/.exec(value.trim());
  return match ? match[1] : undefined;
};

/** Codes of the catalogue layers belonging to the enabled themes. */
export const layerCodesForThemes = (layers, enabledThemes) => (layers || [])
  .filter((layer) => enabledThemes.includes(layer.theme))
  .map((layer) => layer.code);

/**
 * Query of GET /api/uas-zones for a map view; null when the view has no area yet (a map in a hidden
 * container is 0 × 0 px and its bounds collapse to a point, which the server would reject).
 */
export const buildZoneQuery = ({ south, west, north, east, zoom, layerCodes, date }) => {
  const width = east - west;
  const height = north - south;
  if (!(width > 0 && height > 0) || !layerCodes?.length) return null;

  const clampLon = (v) => Math.min(180, Math.max(-180, v));
  const clampLat = (v) => Math.min(90, Math.max(-90, v));
  const bbox = [
    clampLon(west - width * VIEW_PADDING),
    clampLat(south - height * VIEW_PADDING),
    clampLon(east + width * VIEW_PADDING),
    clampLat(north + height * VIEW_PADDING),
  ].map((v) => v.toFixed(6)).join(',');

  const query = { bbox, zoom: Math.max(0, Math.min(22, Math.round(zoom))), layers: layerCodes.join(',') };
  const day = toDateParam(date);
  if (day) query.date = day;
  return query;
};

/**
 * True when the zones already loaded for `loadedQuery` also serve `query` built for `view`: same zoom,
 * layers and date, and the visible view (without its margin) inside the loaded bbox. The server clips
 * zones to the bbox, so a view reaching past it needs a new request.
 */
export const isViewCoveredByQuery = (loadedQuery, query, view) => {
  if (!loadedQuery || !query || !view) return false;
  if (loadedQuery.zoom !== query.zoom || loadedQuery.layers !== query.layers || loadedQuery.date !== query.date) {
    return false;
  }
  const [west, south, east, north] = loadedQuery.bbox.split(',').map(Number);
  return view.west >= west && view.south >= south && view.east <= east && view.north <= north;
};

export const isTooManyZonesError = (error) => error?.response?.status === 400
  && error.response.data?.detail?.error === UAS_ZONES_TOO_MANY;

/** A zone that starts above the ground (e.g. a TMA from FL95) — not a restriction at drone heights by itself. */
export const isElevatedZone = (properties) => properties?.lower_reference !== undefined
  && properties?.lower_reference !== null
  && properties.lower_reference !== 'GND'
  && Number.isFinite(properties.lower_m)
  && properties.lower_m > 0;

/** Colour step of a height-grid cell for its maximum permitted height in metres (see GRID_HEIGHT_STEPS). */
export const gridHeightStep = (upperM) => {
  if (!Number.isFinite(upperM) || upperM < 0) return GRID_UNKNOWN_HEIGHT;
  return GRID_HEIGHT_STEPS.find((step) => upperM >= step.minM);
};

/**
 * Leaflet path style of a zone. Elevated zones are a dashed outline without fill, so the ground-level
 * zones under them stay readable; height-grid cells are coloured by their permitted height.
 */
export const zoneStyle = (layer, properties) => {
  const colors = UAS_ZONE_THEME_COLORS[layer?.theme] || UAS_ZONE_THEME_COLORS.aviation;
  if (layer?.is_height_grid) {
    const step = gridHeightStep(properties?.upper_m);
    return { color: step.color, fillColor: step.color, weight: 0.5, opacity: 0.7, fillOpacity: step.fillOpacity };
  }
  if (isElevatedZone(properties)) {
    return { ...colors, weight: 1, opacity: 0.7, dashArray: '4 4', fillOpacity: 0 };
  }
  return { ...colors, weight: 1.5, opacity: 0.9, fillOpacity: 0.08 };
};

const ringContains = (ring, lng, lat) => {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    if ((yi > lat) !== (yj > lat) && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
};

const polygonContains = (rings, lng, lat) => ringContains(rings[0], lng, lat)
  && !rings.slice(1).some((hole) => ringContains(hole, lng, lat));

/** Whether a Polygon / MultiPolygon GeoJSON geometry contains the point (holes excluded). */
export const geometryContains = (geometry, lng, lat) => {
  if (geometry?.type === 'Polygon') return polygonContains(geometry.coordinates, lng, lat);
  if (geometry?.type === 'MultiPolygon') return geometry.coordinates.some((rings) => polygonContains(rings, lng, lat));
  return false;
};

/** [west, south, east, north] of a Polygon / MultiPolygon GeoJSON geometry; null for anything else. */
export const geometryBounds = (geometry) => {
  const polygons = geometry?.type === 'Polygon' ? [geometry.coordinates]
    : geometry?.type === 'MultiPolygon' ? geometry.coordinates : null;
  if (!polygons?.length) return null;
  let [west, south, east, north] = [Infinity, Infinity, -Infinity, -Infinity];
  polygons.forEach((rings) => (rings[0] || []).forEach(([lng, lat]) => {
    west = Math.min(west, lng); east = Math.max(east, lng);
    south = Math.min(south, lat); north = Math.max(north, lat);
  }));
  return Number.isFinite(west) ? [west, south, east, north] : null;
};

/**
 * Number written in a height-grid cell: its permitted height in metres, placed in the middle of the
 * cell's bounds when that point lies in the cell (grid cells are rectangles). Null when the height is
 * unknown or the middle falls outside (a cell cut into an odd shape).
 */
export const gridCellLabel = (feature) => {
  const upperM = feature?.properties?.upper_m;
  const bounds = geometryBounds(feature?.geometry);
  if (!Number.isFinite(upperM) || !bounds) return null;
  const [west, south, east, north] = bounds;
  const lng = (west + east) / 2;
  const lat = (south + north) / 2;
  if (!geometryContains(feature.geometry, lng, lat)) return null;
  return { text: String(Math.round(upperM)), lng, lat, bounds };
};

/**
 * Font size in px for a cell label of `chars` characters in a cell of `widthPx` × `heightPx` on screen at
 * `zoom`: none below GRID_LABEL_FROM_ZOOM, from it GRID_LABEL_FONT_PX, never more than fits the cell
 * with a margin. Null also below GRID_LABEL_MIN_FONT_PX — the number would not fit.
 */
export const gridLabelFontPx = (widthPx, heightPx, chars, zoom) => {
  if (!(zoom >= GRID_LABEL_FROM_ZOOM)) return null;
  const fits = Math.min((widthPx * 0.8) / (0.62 * Math.max(chars, 1)), heightPx * 0.6);
  const size = Math.floor(Math.min(fits, GRID_LABEL_FONT_PX));
  return size >= GRID_LABEL_MIN_FONT_PX ? size : null;
};

/**
 * HTML of a cell number (a Leaflet divIcon of size 0): black at 45 % opacity, so it blends into the cell
 * colour, centred on the icon's point. Absolutely positioned, so the line box of the map's own font cannot
 * shift it.
 */
export const gridLabelHtml = (label, fontPx) => '<span style="position:absolute;left:0;top:0;'
  + `transform:translate(-50%,-50%);font:700 ${fontPx}px/1 system-ui,sans-serif;color:rgba(0,0,0,0.45);`
  + `white-space:nowrap;pointer-events:none;user-select:none">${escapeHtml(label.text)}</span>`;

/** Zones of a FeatureCollection at a point, ground-level zones first, then by lower limit and upper limit. */
export const zonesAtPoint = (features, lng, lat) => (features || [])
  .filter((feature) => geometryContains(feature.geometry, lng, lat))
  .sort((a, b) => {
    const lowerA = Number.isFinite(a.properties?.lower_m) ? a.properties.lower_m : 0;
    const lowerB = Number.isFinite(b.properties?.lower_m) ? b.properties.lower_m : 0;
    if (lowerA !== lowerB) return lowerA - lowerB;
    const upperA = Number.isFinite(a.properties?.upper_m) ? a.properties.upper_m : Infinity;
    const upperB = Number.isFinite(b.properties?.upper_m) ? b.properties.upper_m : Infinity;
    return upperA - upperB;
  });

export const layerName = (layer, language) => (layer ? (language === 'cs' ? layer.name_cs : layer.name_en) : '');

/** "1. 10. 2026" / "10/1/2026" of a "YYYY-MM-DD" date. */
export const formatZoneDate = (value, language) => {
  const day = toDateParam(value);
  if (!day) return '';
  const [y, m, d] = day.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(language === 'cs' ? 'cs-CZ' : 'en-GB', { timeZone: 'UTC' });
};

/** Newest effective date among the imported layer versions — the data state the map shows. */
export const latestEffectiveFrom = (layers) => (layers || [])
  .map((layer) => layer.version?.effective_from)
  .filter(Boolean)
  .sort()
  .pop() || null;

const escapeHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

const zoneEntryHtml = (layer, properties, language, labels) => {
  const heightLabel = layer?.is_height_grid ? labels.popupMaxHeight : labels.popupHeight;
  const height = layer?.is_height_grid && Number.isFinite(properties?.upper_m)
    ? `${Math.round(properties.upper_m)} m AGL`
    : properties?.vertical_limit;
  return [
    '<div class="py-1">',
    `<p class="font-semibold">${escapeHtml(layerName(layer, language) || properties?.layer)}</p>`,
    `<p class="text-xs font-mono text-slate-600">${escapeHtml(properties?.ident)}</p>`,
    `<p class="text-xs">${escapeHtml(heightLabel)}: ${escapeHtml(height)}</p>`,
    '</div>',
  ].join('');
};

/**
 * HTML of the popup for the zones at a clicked point, in the given order: per zone its layer, ident and
 * height (the grids' value is a ceiling, not the zone's extent), then the date of the ŘLP data shown.
 * `entries` are { layer, properties } with `layer` from the catalogue (may be undefined).
 */
export const zonesPopupHtml = (entries, language, labels = {}) => {
  const dataDate = formatZoneDate(entries.map((e) => e.properties?.effective_from).filter(Boolean).sort().pop(), language);
  return [
    '<div class="text-sm divide-y divide-slate-200" style="min-width:200px;max-height:260px;overflow-y:auto">',
    ...entries.map(({ layer, properties }) => zoneEntryHtml(layer, properties, language, labels)),
    dataDate ? `<p class="text-[11px] text-slate-500 pt-1">${escapeHtml(labels.popupDataAsOf)} ${escapeHtml(dataDate)}</p>` : '',
    '</div>',
  ].join('');
};
